import { useCallback, useEffect, useRef, useState } from "react";
import { getDecryptedItem } from "../../../../utils/localStorageHelper";
import { getLogBotWsUrl } from "./logBotApi";
import {
  aesDecryptFrame,
  aesEncryptFrame,
  createWsE2ESession,
  isE2EEnabled,
} from "./logBotCrypto";
import type {
  CallBrief,
  ChatMessage,
  ClientFrame,
  HistoryTurn,
  ServerFrame,
  ToolCall,
  Usage,
  WsStatus,
} from "./logBotTypes";

const PING_MS = 30_000;
const RECONNECT_BASE_MS = 1_500;
const RECONNECT_MAX_MS = 20_000;

const newId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const historyToMessages = (history: HistoryTurn[]): ChatMessage[] =>
  history.map((h) => ({
    id: newId(),
    role: h.role,
    content: h.content,
    toolCalls: [],
    createdAt: new Date(),
  }));

interface Options {
  callUuid: string;
  sessionId?: string | null;
  enabled?: boolean;
  onAuthFailed?: () => void;
  onSessionReady?: (sessionId: string, resumed: boolean) => void;
}

export const useLogBotWebSocket = ({
  callUuid,
  sessionId = null,
  enabled = true,
  onAuthFailed,
  onSessionReady,
}: Options) => {
  const [status, setStatus] = useState<WsStatus>("connecting");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [callBrief, setCallBrief] = useState<CallBrief | null>(null);
  const [activeTools, setActiveTools] = useState<ToolCall[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(
    sessionId
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [connectKey, setConnectKey] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const passphraseRef = useRef<string | null>(null);
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const sessionIdRef = useRef<string | null>(sessionId);
  const assistantBufferRef = useRef("");
  const streamingMsgIdRef = useRef<string | null>(null);
  /** Bumped on every connect/teardown so late async work is ignored */
  const generationRef = useRef(0);

  const onAuthFailedRef = useRef(onAuthFailed);
  const onSessionReadyRef = useRef(onSessionReady);
  useEffect(() => {
    onAuthFailedRef.current = onAuthFailed;
  }, [onAuthFailed]);
  useEffect(() => {
    onSessionReadyRef.current = onSessionReady;
  }, [onSessionReady]);
  useEffect(() => {
    sessionIdRef.current = sessionId;
    setActiveSessionId(sessionId);
  }, [sessionId]);

  const clearTimers = () => {
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
  };

  const finalizeStreamingOnError = useCallback(() => {
    const id = streamingMsgIdRef.current;
    if (id) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id
            ? {
                ...m,
                content:
                  assistantBufferRef.current ||
                  m.content ||
                  "(interrupted)",
                isStreaming: false,
              }
            : m
        )
      );
    }
    assistantBufferRef.current = "";
    streamingMsgIdRef.current = null;
    setActiveTools([]);
    setIsStreaming(false);
  }, []);

  const sendFrame = useCallback((frame: ClientFrame) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    if (passphraseRef.current) {
      ws.send(
        JSON.stringify({
          data: aesEncryptFrame(frame, passphraseRef.current),
        })
      );
    } else {
      ws.send(JSON.stringify(frame));
    }
  }, []);

  const handleFrame = useCallback(
    (frame: ServerFrame) => {
      switch (frame.type) {
        case "ready": {
          setStatus("ready");
          setCallBrief(frame.call);
          setActiveSessionId(frame.session_id);
          sessionIdRef.current = frame.session_id;
          attemptRef.current = 0;

          if (frame.resumed && frame.history?.length) {
            setMessages(historyToMessages(frame.history));
          } else if (!frame.resumed) {
            // Fresh session after reconnect — drop stale transcript
            setMessages([]);
          }

          onSessionReadyRef.current?.(frame.session_id, frame.resumed);
          break;
        }
        case "tool": {
          setActiveTools((prev) => {
            const idx = prev.findIndex((t) => t.name === frame.name);
            const next: ToolCall = {
              name: frame.name,
              status: frame.status,
              detail: frame.detail,
            };
            if (idx === -1) return [...prev, next];
            const copy = [...prev];
            copy[idx] = next;
            return copy;
          });
          break;
        }
        case "token": {
          setIsStreaming(true);
          assistantBufferRef.current += frame.text;
          const buf = assistantBufferRef.current;

          setMessages((prev) => {
            if (streamingMsgIdRef.current) {
              return prev.map((m) =>
                m.id === streamingMsgIdRef.current
                  ? { ...m, content: buf, isStreaming: true }
                  : m
              );
            }
            const id = newId();
            streamingMsgIdRef.current = id;
            return [
              ...prev,
              {
                id,
                role: "assistant",
                content: buf,
                toolCalls: [],
                isStreaming: true,
                createdAt: new Date(),
              },
            ];
          });
          break;
        }
        case "done": {
          const usage: Usage = frame.usage;
          const content = assistantBufferRef.current;
          const existingId = streamingMsgIdRef.current;
          const finalId = frame.message_id || existingId || newId();

          setMessages((prev) => {
            if (existingId) {
              return prev.map((m) =>
                m.id === existingId
                  ? {
                      ...m,
                      id: finalId,
                      content,
                      isStreaming: false,
                      usage,
                    }
                  : m
              );
            }
            // done with no prior tokens — still show a bubble
            return [
              ...prev,
              {
                id: finalId,
                role: "assistant",
                content: content || "",
                toolCalls: [],
                isStreaming: false,
                usage,
                createdAt: new Date(),
              },
            ];
          });

          assistantBufferRef.current = "";
          streamingMsgIdRef.current = null;
          setActiveTools([]);
          setIsStreaming(false);
          break;
        }
        case "error": {
          setError(frame.detail || "Something went wrong");
          finalizeStreamingOnError();
          break;
        }
        case "pong":
          break;
        default:
          break;
      }
    },
    [finalizeStreamingOnError]
  );

  const connect = useCallback(async () => {
    if (!enabled || !callUuid) return;

    const generation = ++generationRef.current;
    intentionalCloseRef.current = false;
    clearTimers();
    setStatus("connecting");
    setError(null);

    const token = getDecryptedItem("token");
    if (!token) {
      setStatus("error");
      setError("Not authenticated");
      onAuthFailedRef.current?.();
      return;
    }

    let passphrase: string | null = null;
    let xApiHeaders: string | null = null;

    try {
      if (isE2EEnabled()) {
        const e2e = await createWsE2ESession();
        if (generation !== generationRef.current) return;

        if (!e2e) {
          setStatus("error");
          setError(
            "Could not establish encrypted connection. Please refresh and try again."
          );
          return;
        }
        passphrase = e2e.passphrase;
        xApiHeaders = e2e.xApiHeaders;
      }
    } catch (err) {
      console.error("LogBot E2E session setup failed:", err);
      if (generation !== generationRef.current) return;
      if (isE2EEnabled()) {
        setStatus("error");
        setError(
          "Could not establish encrypted connection. Please refresh and try again."
        );
        return;
      }
    }

    if (generation !== generationRef.current) return;

    passphraseRef.current = passphrase;

    const params = new URLSearchParams();
    if (xApiHeaders) {
      params.set("x-api-headers", xApiHeaders);
    }

    const qs = params.toString();
    const url = `${getLogBotWsUrl()}${qs ? `?${qs}` : ""}`;
    const ws = new WebSocket(url);

    if (generation !== generationRef.current) {
      ws.close();
      return;
    }

    wsRef.current = ws;

    ws.onopen = () => {
      if (generation !== generationRef.current) {
        ws.close();
        return;
      }

      const init: ClientFrame = {
        type: "init",
        call_uuid: callUuid,
        token,
        ...(sessionIdRef.current ? { session_id: sessionIdRef.current } : {}),
      };
      if (passphrase) {
        ws.send(JSON.stringify({ data: aesEncryptFrame(init, passphrase) }));
      } else {
        ws.send(JSON.stringify(init));
      }

      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          if (passphrase) {
            ws.send(
              JSON.stringify({
                data: aesEncryptFrame({ type: "ping" }, passphrase),
              })
            );
          } else {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }
      }, PING_MS);
    };

    ws.onmessage = (evt) => {
      if (generation !== generationRef.current) return;
      try {
        const raw = JSON.parse(evt.data);
        let frame: ServerFrame | null = null;

        if (raw?.data && passphraseRef.current) {
          frame = aesDecryptFrame<ServerFrame>(
            raw.data,
            passphraseRef.current
          );
        } else if (raw?.type) {
          frame = raw as ServerFrame;
        }

        if (frame) handleFrame(frame);
      } catch (err) {
        console.error("LogBot WS parse error:", err);
      }
    };

    ws.onclose = (e) => {
      if (generation !== generationRef.current) return;

      clearTimers();
      if (wsRef.current === ws) wsRef.current = null;

      if (e.code === 1008) {
        setStatus("error");
        setError("Authentication failed. Please log in again.");
        onAuthFailedRef.current?.();
        return;
      }

      if (intentionalCloseRef.current) {
        setStatus("closed");
        return;
      }

      setStatus("connecting");
      const delay = Math.min(
        RECONNECT_BASE_MS * Math.pow(2, attemptRef.current),
        RECONNECT_MAX_MS
      );
      attemptRef.current += 1;
      reconnectRef.current = setTimeout(() => {
        if (generation === generationRef.current) connect();
      }, delay);
    };

    ws.onerror = () => {
      // onclose handles reconnect / status
    };
  }, [callUuid, enabled, handleFrame]);

  useEffect(() => {
    if (!enabled) return;

    setMessages([]);
    setActiveTools([]);
    assistantBufferRef.current = "";
    streamingMsgIdRef.current = null;
    setIsStreaming(false);

    connect();

    return () => {
      intentionalCloseRef.current = true;
      generationRef.current += 1;
      clearTimers();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callUuid, sessionId, enabled, connectKey]);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || status !== "ready" || isStreaming) return;

      setError(null);
      assistantBufferRef.current = "";
      streamingMsgIdRef.current = null;

      setMessages((prev) => [
        ...prev,
        {
          id: newId(),
          role: "user",
          content: trimmed,
          toolCalls: [],
          createdAt: new Date(),
        },
      ]);

      sendFrame({ type: "message", text: trimmed });
    },
    [status, isStreaming, sendFrame]
  );

  const clearError = useCallback(() => setError(null), []);

  const startNewSession = useCallback(() => {
    sessionIdRef.current = null;
    setActiveSessionId(null);
    setConnectKey((k) => k + 1);
  }, []);

  return {
    status,
    messages,
    callBrief,
    activeTools,
    error,
    activeSessionId,
    isStreaming,
    sendMessage,
    clearError,
    startNewSession,
    setMessages,
  };
};
